// promptGallery — curated catalog of prompts tailored for Symbols projects.
//
// Route: /prompt-gallery
//
// Standalone Page (no MarketplaceIsland chrome). Mirrors the layout
// vocabulary of the rest of marketplace: header → filter chips →
// responsive card grid. Each card surfaces a copy-to-clipboard prompt
// the user can drop straight into Claude / the Symbols MCP / the
// editor’s AI tab.
//
// PROMPTS lives next to the page (rather than data/catalog.js) because
// gallery prompts evolve with the framework, not with the marketplace
// catalog table — this file is the single source of truth.
//
// State (`state.activeCategory`, `state._promptGalleryCopiedId`) is
// declared on the page so PromptGalleryChip + PromptGalleryCard can
// read/write via the `s` parameter without walking to the app root.

export const PROMPTS = [
  // ── Components ────────────────────────────────────────────────────────────
  {
    id: 'cmp.button-variants',
    category: 'components',
    title: 'Button with variants',
    tagline: 'Primary / secondary / ghost variants in v3.14 syntax.',
    prompt:
      'Create a Button component for a Symbols project using DOMQL v3.14. ' +
      'Support primary, secondary, and ghost variants via a `variant` prop. ' +
      'Use design system tokens for color, padding, and round. ' +
      'Hover and :focus-visible states should use DS tokens (no raw colors). ' +
      'No `attr:` wrapper for `disabled` / `type` — flat props only.'
  },
  {
    id: 'cmp.card-collection',
    category: 'components',
    title: 'Card grid from data',
    tagline: 'children + childExtends pattern (no $collection).',
    prompt:
      'Build a responsive card grid in Symbols v3.14 that renders cards from a ' +
      '`children` array (objects with title, description, thumbnail). Use ' +
      '`childExtends: "Card"` and design system tokens for spacing. ' +
      'Cards should expose a hover border state and an Enter/Space keyboard ' +
      'handler (tabindex + role="button"). No raw px values.'
  },
  {
    id: 'cmp.modal',
    category: 'components',
    title: 'Modal with backdrop',
    tagline: 'Esc-to-close, focus trap, design tokens only.',
    prompt:
      'Implement a Modal component in DOMQL v3.14 with a backdrop, Esc-to-close ' +
      'handling registered in onCreate / unregistered in onRemove, and a ' +
      'focus trap on the first interactive element. Use DS tokens for round, ' +
      'padding, background, and shadow. Mount via a state flag on the host.'
  },

  // ── Pages ─────────────────────────────────────────────────────────────────
  {
    id: 'pg.dashboard',
    category: 'pages',
    title: 'Dashboard page scaffold',
    tagline: 'AppShell + AppSidebar + AppMain composition.',
    prompt:
      'Generate a dashboard page for a Symbols project that extends `Page`, ' +
      'composes `AppShell` chrome, has a left `AppSidebar` with a vertical ' +
      'nav, and an `AppMain` body with a header row + grid of stat cards. ' +
      'Use `el.router(path, el.getRoot())` for nav clicks (never ' +
      '`window.location`). Stick to v3.14 flat handlers (`onClick`, not `on.click`).'
  },
  {
    id: 'pg.settings',
    category: 'pages',
    title: 'Settings page with sections',
    tagline: 'Tabbed sub-routes, form fields, save bar.',
    prompt:
      'Build a settings page with tabbed sub-sections (Profile, Workspace, ' +
      'Integrations, Danger Zone). Each tab is its own sub-page reachable ' +
      'via `/settings/<slug>`. Forms use top-level HTML attribute props ' +
      '(`placeholder`, `type`, `name`, `value`) — never wrapped in `attr:`. ' +
      'Sticky save bar at the bottom that gates on a dirty-state flag.'
  },
  {
    id: 'pg.empty-state',
    category: 'pages',
    title: 'Empty state with CTA',
    tagline: 'Onboarding-grade empty page with illustration and primary action.',
    prompt:
      'Compose an empty-state page in DOMQL v3.14 — centered illustration ' +
      '(an Icon at 5–6 sizes), a heading, two-line caption, primary CTA ' +
      'button, and a "Browse examples" secondary link. Use DS color tokens ' +
      '(`title`, `caption`, `blue`) and spacing tokens only. No raw px.'
  },

  // ── Design system ─────────────────────────────────────────────────────────
  {
    id: 'ds.theme-extend',
    category: 'design-system',
    title: 'Extend the design system',
    tagline: 'Add a new color, type ramp step, or spacing token cleanly.',
    prompt:
      'I need to extend the design system in a Symbols project. Walk me ' +
      'through adding (a) a new color (with light/dark tokens and shading ' +
      'modifiers like `.7`, `+50`), (b) a typography step, and (c) a spacing ' +
      'token. Show me where each lives under `designSystem/` and confirm ' +
      'lowercase keys. No hex colors hardcoded in components afterwards.'
  },
  {
    id: 'ds.dark-mode',
    category: 'design-system',
    title: 'Dark / light theme switch',
    tagline: 'Per-component @dark variants without raw selectors.',
    prompt:
      'Show how to apply per-component dark-mode overrides in DOMQL v3.14. ' +
      'Use the `\'@dark\': { \':hover\': {...} }` nested syntax — never ' +
      'chained selectors like `\'@dark :hover\'`. Wire up a theme switcher ' +
      'that toggles via `el.call(\'toggleTheme\', target)`.'
  },
  {
    id: 'ds.audit',
    category: 'design-system',
    title: 'Audit a component for token compliance',
    tagline: 'Strip raw px / hex / Tailwind palettes; report violations.',
    prompt:
      'Audit this Symbols component (paste below) for design-system ' +
      'compliance: replace any raw px / rem / hex / rgba with DS tokens, ' +
      'remove `!important`, replace any Tailwind-style palette names ' +
      '(`blue-500`) with shading modifiers (`blue.7`), and confirm all ' +
      'spacing uses DS tokens (X, Y, Z, A, B…). Report each violation with ' +
      'a one-line fix.\n\nComponent:\n```js\n// paste here\n```'
  },

  // ── Migration ─────────────────────────────────────────────────────────────
  {
    id: 'mig.v3-v3.14',
    category: 'migration',
    title: 'Migrate v3 → v3.14 syntax',
    tagline: 'el.props.X → el.X, on.click → onClick, attr → flat props.',
    prompt:
      'Migrate this DOMQL v3 component to v3.14 (paste below). Required ' +
      'transforms:\n' +
      '  • `el.props.X` → `el.X` everywhere (declaration AND runtime)\n' +
      '  • `{ on: { click: ... } }` → `{ onClick: ... }` (also `init`, `render`)\n' +
      '  • Flatten `attr: { placeholder, type, name, value, disabled }` to top-level props\n' +
      '  • Drop redundant `extends:` when the key already matches\n' +
      '  • Replace any project file imports with `el.call(\'fnName\', ...)`\n' +
      'Do NOT leave any `el.props.` / `on.` / `attr:` patterns behind.'
  },
  {
    id: 'mig.react-to-domql',
    category: 'migration',
    title: 'Convert React → DOMQL',
    tagline: 'JSX, useState, hooks → plain object component + state model.',
    prompt:
      'Convert this React component to DOMQL v3.14 for a Symbols project ' +
      '(paste below). Translate JSX to nested DOMQL keys, replace useState ' +
      'with state object + s.update, replace event handlers with `onClick` / ' +
      '`onInput` flat handlers receiving `(e, el, s)`, and replace any ' +
      'Tailwind classes with DS tokens. No imports between project files.'
  },

  // ── Integrations ──────────────────────────────────────────────────────────
  {
    id: 'int.install-rail',
    category: 'integrations',
    title: 'Mount InstallableRail in a project',
    tagline: 'Drop a workspace integration rail into any page.',
    prompt:
      'Show me how to mount an `InstallableRail` in a Symbols workspace ' +
      'page. I want the integration domain, scoped to the current ' +
      'workspace, in the rail layout, with a custom title. List which ' +
      'state keys it reads from the root and how it dispatches install ' +
      'modal opens (`openInstallableModal`).'
  },
  {
    id: 'int.openid',
    category: 'integrations',
    title: 'Wire an OAuth integration',
    tagline: 'Register a kind, add post-install hook, expose to MarketplaceCard.',
    prompt:
      'I want to add a new OAuth integration to the Symbols marketplace. ' +
      'Walk me through (1) inserting the kind row in `integration_kinds`, ' +
      '(2) registering the kind in the static catalog, (3) the OAuth ' +
      'callback handler, and (4) the post-install `onInstalled` hook ' +
      '(which fires AFTER confirm, not optimistically).'
  },

  // ── Debugging ─────────────────────────────────────────────────────────────
  {
    id: 'dbg.blank-render',
    category: 'debugging',
    title: 'Component renders blank in v3.14',
    tagline: 'Most common cause: leftover el.props / el.on access after migration.',
    prompt:
      'My DOMQL v3.14 component silently renders blank. Diagnose by checking, ' +
      'in order: (1) leftover `el.props.X` reads (v3.14 removed them — use ' +
      '`el.X`), (2) `on.event` handlers (v3.14 uses `onEvent`), (3) `attr:` ' +
      'wrappers swallowing flat props, (4) extends key collisions, ' +
      '(5) state read from the wrong host (check `marketplaceHostOf` / ' +
      '`*HostOf` walkers). Paste the component below.'
  },
  {
    id: 'dbg.iframe-leak',
    category: 'debugging',
    title: 'Canvas iframe sharing design system with parent',
    tagline: 'Scratch singleton bleed — isolate via context.designSystem.',
    prompt:
      'My canvas-hosted iframe project app is leaking the canvas editor\'s ' +
      'design system (or vice versa). Help me trace the Scratch ' +
      'module-level singleton (`getActiveConfig` in scratch/src/factory.js) ' +
      'and show how to thread an isolated config via ' +
      '`element.context.designSystem` instead of relying on the global.'
  },

  // ── Refactoring ───────────────────────────────────────────────────────────
  {
    id: 'rf.extract-component',
    category: 'refactoring',
    title: 'Extract a sub-tree into a registered component',
    tagline: 'Promote inline keys to a project-level component with PascalCase.',
    prompt:
      'Extract this inline DOMQL sub-tree (paste below) into a named ' +
      'project-level component. Place it in `components/<Name>.js`, export ' +
      'with the PascalCase key, and reference it from the consumer by key ' +
      'name (no import — the framework auto-extends by key). Drop any ' +
      'redundant `extends:` once the key matches.'
  },
  {
    id: 'rf.shared-library',
    category: 'refactoring',
    title: 'Promote a component to shared library',
    tagline: 'Move a duplicated component into shared/ with the right export shape.',
    prompt:
      'I have the same component duplicated across two Symbols projects. ' +
      'Promote it into the appropriate `shared` package, ensure it is ' +
      'exported through the package context, confirm `sharedLibraries.js` ' +
      'in each consumer already includes that package, and remove the ' +
      'local copies. Do NOT re-export the component from the consumer.'
  }
]

// Object-keyed so each chip element gets `el.key` (`'all'`, `'components'`, …).
// Entry order tracks the PROMPTS section order so the chip rail mirrors
// how the catalog reads top-to-bottom.
export const PROMPT_CATEGORIES = {
  all: { label: 'All' },
  components: { label: 'Components' },
  pages: { label: 'Pages' },
  'design-system': { label: 'Design System' },
  migration: { label: 'Migration' },
  integrations: { label: 'Integrations' },
  debugging: { label: 'Debugging' },
  refactoring: { label: 'Refactoring' }
}

const _filteredPrompts = (cat) =>
  cat === 'all' ? PROMPTS : PROMPTS.filter((p) => p.category === cat)

export const promptGallery = {
  extends: 'Page',
  flow: 'y',
  gap: 'D',
  padding: 'C E',
  overflowY: 'auto',
  background: 'document',

  state: {
    activeCategory: 'all',
    _promptGalleryCopiedId: null
  },

  // ── Header ────────────────────────────────────────────────────────────────

  Header: {
    flow: 'y',
    gap: 'Z',
    maxWidth: 'I3',

    Eyebrow: {
      tag: 'span',
      text: 'Prompt Gallery',
      fontSize: 'Y',
      fontWeight: '600',
      color: 'blue',
      textTransform: 'uppercase',
      letterSpacing: 'X'
    },

    Title: {
      tag: 'h1',
      margin: '0',
      fontSize: 'F',
      fontWeight: '800',
      color: 'title',
      lineHeight: '1.1',
      text: 'Prompts that work for Symbols projects'
    },

    Subtitle: {
      tag: 'p',
      margin: '0',
      fontSize: 'A',
      color: 'caption',
      lineHeight: '1.5',
      text:
        'Curated, copy-ready prompts for building, migrating, and ' +
        'debugging DOMQL v3.14 components, pages, and design systems. ' +
        'Tap any card to copy the full prompt — paste it into Claude, the ' +
        'Symbols MCP, or the editor’s AI tab.'
    }
  },

  // ── Filter chip rail ──────────────────────────────────────────────────────

  FilterRail: {
    flow: 'x',
    gap: 'Y',
    flexWrap: 'wrap',
    padding: 'Z 0',
    borderTop: '1px solid line',
    borderBottom: '1px solid line',
    childExtends: 'PromptGalleryChip',
    children: PROMPT_CATEGORIES
  },

  // ── Results meta line ─────────────────────────────────────────────────────

  ResultsLine: {
    tag: 'p',
    margin: '0',
    fontSize: 'Y2',
    color: 'caption',
    text: (el, s) => {
      const total = PROMPTS.length
      const cat = s.activeCategory
      if (cat === 'all') return `${total} prompts · all categories`
      const filtered = _filteredPrompts(cat).length
      const label = (PROMPT_CATEGORIES[cat] || {}).label || cat
      return `${filtered} of ${total} prompts · ${label}`
    }
  },

  // ── Grid ──────────────────────────────────────────────────────────────────

  Grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
    gap: 'A',
    childExtends: 'PromptGalleryCard',
    children: (el, s) => {
      const visible = _filteredPrompts(s.activeCategory)
      if (visible.length === 0) {
        return [{
          extends: 'Flex',
          flow: 'y',
          gap: 'Z',
          align: 'center center',
          padding: 'D',
          gridColumn: '1 / -1',
          EmptyTitle: {
            tag: 'h3',
            margin: '0',
            fontSize: 'B',
            color: 'title',
            text: 'No prompts in this category yet'
          },
          EmptyHint: {
            tag: 'p',
            margin: '0',
            fontSize: 'Z2',
            color: 'caption',
            text: 'Switch to All to see every prompt.'
          }
        }]
      }
      return visible
    }
  }
}
